import type { IAddressConstructorParams } from "@services/sdk";
import { getMaybeUndefined } from "@services/sdk/helpers";
import Address from "./address";

interface IAccountConstructorParams {
	account: {
		isEmailVerified: boolean;
		accountNumber?: string;
		newUnverifiedEmail?: string;
	};
	relatedParty: {
		firstName: string;
		preferredName?: string;
		lastName?: string;
		email?: string;
		password?: string;
		dob?: string;
	};
	postalAddress?: IAddressConstructorParams;
	billingAddress?: IAddressConstructorParams;
}

class Account {
	public static EmptyConstructorParams: IAccountConstructorParams = {
		account: {
			isEmailVerified: false,
			accountNumber: "",
		},
		relatedParty: {
			firstName: "",
			lastName: "",
			email: "",
			password: "",
			dob: "",
		},
		postalAddress: Address.EmptyConstructorParams,
		billingAddress: Address.EmptyConstructorParams,
	};

	responseData: IAccountConstructorParams;

	public static ConstructEmpty(): Account {
		return Account.Construct(Account.EmptyConstructorParams);
	}

	public static Construct(params: IAccountConstructorParams): Account {
		return new Account(params);
	}

	constructor(params: IAccountConstructorParams) {
		this.responseData = params;
	}

	get displayedName(): string {
		if (this.preferredName !== "-") {
			return this.preferredName;
		}

		return this.firstName;
	}

	get firstName(): string {
		return getMaybeUndefined(this.responseData.relatedParty.firstName, "-");
	}

	get lastName(): string {
		return getMaybeUndefined(this.responseData.relatedParty.lastName, "-");
	}

	get preferredName(): string {
		return getMaybeUndefined(this.responseData.relatedParty.preferredName, "-");
	}

	get dob(): string | null {
		return getMaybeUndefined(this.responseData.relatedParty.dob, null);
	}

	get email(): string | null {
		return getMaybeUndefined(this.responseData.relatedParty.email, null);
	}

	get isEmailVerified(): boolean {
		return this.responseData.account.isEmailVerified;
	}

	get isInitialised(): boolean {
		return this.responseData.account.accountNumber !== "";
	}

	get postalAddress(): Address {
		if (!this.responseData.postalAddress) {
			return Address.ConstructEmpty();
		}

		return Address.Construct(this.responseData.postalAddress);
	}

	get billingAddress(): Address {
		if (!this.responseData.billingAddress) {
			return Address.ConstructEmpty();
		}

		return Address.Construct(this.responseData.billingAddress);
	}

	get accountNumber(): string {
		return getMaybeUndefined(this.responseData.account.accountNumber, "-");
	}

	get newUnverifiedEmail(): string | null {
		return getMaybeUndefined(
			this.responseData.account.newUnverifiedEmail,
			null,
		);
	}
}

export default Account;
